/* eslint-disable max-lines */
import React, {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {Challenge, ChallengeMode, ChoiceUserInputAction, ChoiceUserInputActionInput, Scalars} from "../../../schema";
import {useFormState} from "../../../hooks/useFormState";
import {useQuery} from "@apollo/client";

import {INPUT_PHRASE_MAX_LENGTH} from "../../../settings";
import {ActionInput} from "../../../types";
import Button from "../../../ui/button";
import WithTooltip from "../../../ui/tooltip";
import {HelpCircleIcon} from "../../../ui/icons";
import LimitedTextarea from "../LimitedTextarea";
import Link from "../../../ui/link";
import {supportedPhraseTags} from "../../Phrase";
import {competenciesQuery, competenciesQueryData} from "./ActionEditForm.graphql";
import CompetenciesCreatableSelect from "../CompetenciesCreatableSelect";

import classes from "./forms.module.css";

type opts = {
  action: ChoiceUserInputAction;
  testId: string;
  submitDisabled?: boolean;
  onSubmit: (data: ActionInput) => void;
  challenge: Challenge;
  phraseLimit?: Scalars["Int"];
};

type formState = Omit<ChoiceUserInputActionInput, "beforeActionId" | "freeSpeech"> & {
  questionText: string;
  hintText: string;
};

export default function ChoiceUserInputQuestionEditForm({action, testId, challenge, phraseLimit, onSubmit}: opts) {
  const {t} = useTranslation();

  const {competencies} = useQuery<competenciesQueryData, competenciesQueryData>(competenciesQuery).data ?? {};

  const [hintTextVisibility, setHintTextVisibility] = useState(!!action.hintText);
  const showHintTextArea = useCallback(() => setHintTextVisibility(true), []);

  const formState = useFormState<formState>({
    initialValues: {
      questionText: action.questionText ?? "",
      hintText: action.hintText ?? "",
      competenceId: action.competence?.id ?? undefined,
    },
    preventDefault: true,
    onSubmit: (data) => {
      onSubmit &&
        onSubmit({
          ...data,
          hintText: data.hintText,
          questionText: data.questionText,
        });
    },
  });

  const updateCompetence = useCallback(
    (id?: string) => {
      formState.setValues({competenceId: id ?? null});
    },
    [formState],
  );

  return (
    <form data-testid={`ChoiceUserInputQuestionEditForm:${testId}`} method="post" onSubmit={formState.submitHandler}>
      {challenge.mode === ChallengeMode.COMPETENCE_TEST && (
        <div className={classes.row}>
          <label className={classes.label}>
            {t("components.ActionEditForm.competence.label")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ActionEditForm.competence.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ActionEditForm.competence.helpText")}
          >
            <CompetenciesCreatableSelect
              competencies={competencies ?? []}
              currentItemIds={formState.values.competenceId ?? undefined}
              onSelect={updateCompetence}
              isMulti={false}
            />
          </WithTooltip>
        </div>
      )}
      <div className={classes.row}>
        <label className={classes.label} htmlFor="questionText">
          {t("components.ChoiceUserInputQuestionEditForm.questionText.title")}
        </label>
        <LimitedTextarea
          maxLength={phraseLimit ?? INPUT_PHRASE_MAX_LENGTH}
          filters={supportedPhraseTags}
          id="questionText"
          name="questionText"
          rows={4}
          value={formState.values.questionText!}
          onChange={formState.changeHandler}
          placeholder={t("components.ChoiceUserInputQuestionEditForm.questionText.placeholder")}
        />
      </div>

      {!hintTextVisibility && (
        <div className={classes.row}>
          <Link onClick={showHintTextArea}>{t("components.ChoiceUserInputQuestionEditForm.hintText.showArea")}</Link>
        </div>
      )}

      {hintTextVisibility && (
        <div className={classes.row}>
          <label className={classes.label} htmlFor="hintText">
            {t("components.ChoiceUserInputQuestionEditForm.hintText.title")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ChoiceUserInputQuestionEditForm.hintText.help")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <LimitedTextarea
            maxLength={50}
            id="hintText"
            name="hintText"
            rows={4}
            value={formState.values.hintText}
            onChange={formState.changeHandler}
            placeholder={t("components.ChoiceUserInputQuestionEditForm.hintText.placeholder")}
          />
        </div>
      )}

      <div className={classes.row}>
        <Button color={"success"}>{t("common.save")}</Button>
      </div>
    </form>
  );
}
